import React from 'react';

// Shared compliance-document configuration fields, embedded by BOTH the
// Fusion Documents Upload modal (create) and Configure modal (edit). Lifted from
// the retired Risk Flag DocumentConfigDrawer so the two consumers render one
// consistent set of controls:
//
//   Expiry & Validity  — validity_value/unit, expiring_soon_value/unit,
//                        supplier_provides_expiry
//   Upload Rules       — accepted_file_types, max_file_size_mb
//   Auto-Reminder Rules — document_reminder_rules (timing/cadence sentences)
//   Enforcement        — block_expired_from_briefs, auto_flag_red_when_expired
//
// The consumer owns the flat `fields` object + `rules` array in its own state
// (seed with blankComplianceFields / seedComplianceFields) and persists them:
// the fields onto fusion_documents, the rules via wipe+reinsert into
// document_reminder_rules keyed by compliance_doc_key. "Who provides this
// document?" is intentionally NOT here — that stays the modal's own
// satisfied_by control (Upload vs Acknowledge), the canonical field.

export const VALIDITY_UNITS = ['days', 'months', 'years'];
export const EXPIRING_UNITS = ['days', 'weeks', 'months'];
export const FILE_TYPES = [
  { value: 'pdf',   label: 'PDF' },
  { value: 'image', label: 'JPG / PNG' },
  { value: 'docx',  label: 'DOCX' },
  { value: 'xlsx',  label: 'XLSX' },
];
const REMINDER_TIMING_UNITS = ['days', 'weeks', 'months'];
const REMINDER_DIRECTIONS = [
  { value: 'before', label: 'before' },
  { value: 'after',  label: 'after' },
  { value: 'on',     label: 'on' },
];
const REMINDER_CADENCES = [
  { value: 'one_time',           label: 'one time' },
  { value: 'recurring_weekly',   label: 'recurring weekly' },
  { value: 'recurring_monthly',  label: 'recurring monthly' },
];

export const DEFAULT_REMINDER_RULES = [
  { timing_value: 30, timing_unit: 'days', timing_direction: 'before', cadence: 'one_time' },
  { timing_value: 7,  timing_unit: 'days', timing_direction: 'before', cadence: 'one_time' },
  { timing_value: 0,  timing_unit: 'days', timing_direction: 'on',     cadence: 'one_time' },
  { timing_value: 14, timing_unit: 'days', timing_direction: 'after',  cadence: 'recurring_weekly' },
];

// Flat compliance-config defaults for a NEW document.
export const blankComplianceFields = () => ({
  validity_value: 12,
  validity_unit: 'months',
  expiring_soon_value: 30,
  expiring_soon_unit: 'days',
  supplier_provides_expiry: true,
  accepted_file_types: ['pdf', 'image'],
  max_file_size_mb: 10,
  block_expired_from_briefs: true,
  auto_flag_red_when_expired: false,
  approval_mode: 'manual',
  sample_review_pct: 10,
});

// Auto-approval SUITS documents where the supplier submits no artefact of their
// own: the content is Fusion's and the identity fields come from their account,
// so there's nothing for a reviewer to open. Those methods get it preset on.
//
// It's offered everywhere else too (Rob, 2026-08-12) — an upload can be set to
// auto-approve if that's the call — but the UI says plainly that nobody will
// read the file. The real guard is the expiry exception: any submission
// carrying an expiry date goes to a person regardless, because approving one
// confirms that date.
export const AUTO_APPROVAL_RECOMMENDED = ['form', 'acknowledge', 'pdf_sign'];
export const autoApprovalRecommended = (satisfiedBy) => AUTO_APPROVAL_RECOMMENDED.includes(satisfiedBy || 'upload');

// Map an existing fusion_documents row → flat compliance-config fields, with
// defaults where a column is null (legacy rows predate these columns).
export const seedComplianceFields = (doc) => {
  if (!doc) return blankComplianceFields();
  return {
    validity_value: doc.validity_value ?? '',
    validity_unit: doc.validity_unit || 'months',
    expiring_soon_value: doc.expiring_soon_value ?? 30,
    expiring_soon_unit: doc.expiring_soon_unit || 'days',
    supplier_provides_expiry: doc.supplier_provides_expiry ?? true,
    accepted_file_types: Array.isArray(doc.accepted_file_types) && doc.accepted_file_types.length > 0
      ? doc.accepted_file_types
      : ['pdf', 'image'],
    max_file_size_mb: doc.max_file_size_mb ?? 10,
    block_expired_from_briefs: doc.block_expired_from_briefs ?? true,
    auto_flag_red_when_expired: doc.auto_flag_red_when_expired ?? false,
    approval_mode: doc.approval_mode || 'manual',
    sample_review_pct: doc.sample_review_pct ?? 10,
  };
};

// Normalise before persisting: blank validity_value → null ("never expires").
export const normaliseComplianceFields = (fields) => ({
  validity_value: fields.validity_value === '' || fields.validity_value == null ? null : Number(fields.validity_value),
  validity_unit: fields.validity_unit || 'months',
  expiring_soon_value: fields.expiring_soon_value === '' || fields.expiring_soon_value == null ? 30 : Number(fields.expiring_soon_value),
  expiring_soon_unit: fields.expiring_soon_unit || 'days',
  supplier_provides_expiry: !!fields.supplier_provides_expiry,
  accepted_file_types: Array.isArray(fields.accepted_file_types) && fields.accepted_file_types.length > 0 ? fields.accepted_file_types : ['pdf'],
  max_file_size_mb: fields.max_file_size_mb === '' || fields.max_file_size_mb == null ? 10 : Number(fields.max_file_size_mb),
  block_expired_from_briefs: !!fields.block_expired_from_briefs,
  auto_flag_red_when_expired: !!fields.auto_flag_red_when_expired,
  approval_mode: fields.approval_mode === 'auto' ? 'auto' : 'manual',
  // Clamp rather than trust the input: this number decides how much of the
  // review queue you actually see.
  sample_review_pct: Math.max(0, Math.min(100,
    fields.sample_review_pct === '' || fields.sample_review_pct == null ? 10 : Number(fields.sample_review_pct))),
});

export default function ComplianceConfigFields({ fields, setField, rules, setRules, satisfiedBy }) {
  const toggleFileType = (value) => {
    const next = new Set(fields.accepted_file_types || []);
    if (next.has(value)) next.delete(value); else next.add(value);
    setField('accepted_file_types', Array.from(next));
  };
  const updateRule = (idx, patch) => setRules(rules.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeRule = (idx) => setRules(rules.filter((_, i) => i !== idx));
  const addRule = () => setRules([...rules, { timing_value: 7, timing_unit: 'days', timing_direction: 'before', cadence: 'one_time' }]);

  return (
    <>
      {/* ============ EXPIRY & VALIDITY ============ */}
      <Section label="Expiry & Validity">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Document is valid for" hint="From the document's start date. Leave blank for documents that don't expire.">
            <NumberWithUnit
              value={fields.validity_value}
              unit={fields.validity_unit}
              onValue={v => setField('validity_value', v)}
              onUnit={v => setField('validity_unit', v)}
              units={VALIDITY_UNITS} />
          </Field>
          <Field label='"Expiring Soon" threshold' hint="Flags amber when within this period of expiry.">
            <NumberWithUnit
              value={fields.expiring_soon_value}
              unit={fields.expiring_soon_unit}
              onValue={v => setField('expiring_soon_value', v)}
              onUnit={v => setField('expiring_soon_unit', v)}
              units={EXPIRING_UNITS} />
          </Field>
        </div>
        <Toggle
          checked={fields.supplier_provides_expiry}
          onChange={v => setField('supplier_provides_expiry', v)}
          label="Supplier must provide expiry date on upload"
          sub="When off, admins set expiry on review." />
      </Section>

      {/* ============ UPLOAD RULES ============ */}
      <Section label="Upload Rules">
        <Field label="Accepted file types" hint="Selected types are accepted on supplier upload.">
          <div className="flex gap-1.5 flex-wrap">
            {FILE_TYPES.map(ft => {
              const active = (fields.accepted_file_types || []).includes(ft.value);
              return (
                <button
                  key={ft.value}
                  type="button"
                  onClick={() => toggleFileType(ft.value)}
                  className={`font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase px-3 py-1.5 rounded-full border transition-colors ${active
                    ? 'bg-[#92D400]/10 border-[#92D400] text-fusion-forest'
                    : 'bg-white border-line text-fusion-mid-grey hover:border-[#DADCCD]'}`}>
                  {ft.label}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Max file size">
          <NumberWithUnit
            value={fields.max_file_size_mb}
            unit="MB"
            onValue={v => setField('max_file_size_mb', v === '' ? '' : Number(v))}
            onUnit={() => {}}
            units={['MB']} />
        </Field>
      </Section>

      {/* ============ AUTO-REMINDER RULES ============ */}
      <Section label="Auto-Reminder Rules">
        <div className="font-['Metropolis'] text-xs text-[#51534A] -mt-1 mb-3.5 leading-relaxed">
          When the system automatically nudges suppliers about this document. You'll see a record of every reminder in the audit log.
        </div>
        {rules.length === 0 && (
          <div className="font-['Metropolis'] text-xs text-[#51534A] mb-2 italic">
            No reminder rules configured — suppliers won't receive automatic nudges for this document.
          </div>
        )}
        {rules.map((rule, idx) => (
          <ReminderRuleRow key={idx} rule={rule} onChange={patch => updateRule(idx, patch)} onRemove={() => removeRule(idx)} />
        ))}
        <button
          type="button"
          onClick={addRule}
          className="w-full mt-1.5 bg-transparent border border-dashed border-line rounded font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-fusion-forest py-2.5 hover:border-active-green hover:bg-active-green/[0.04] transition-colors">
          + Add reminder rule
        </button>
      </Section>

      {/* ============ ENFORCEMENT ============ */}
      {/* ============ REVIEW ============ */}
      {(
        <Section label="Review">
          <Toggle
            checked={fields.approval_mode === 'auto'}
            onChange={v => setField('approval_mode', v ? 'auto' : 'manual')}
            label={autoApprovalRecommended(satisfiedBy)
              ? 'Approve submissions automatically (suggested for this type)'
              : 'Approve submissions automatically'}
            sub={autoApprovalRecommended(satisfiedBy)
              ? "The content is ours and the details come from the supplier's account, so there's nothing for a reviewer to open. Submissions are approved on arrival and stamped 'auto-approved by rule' in the document history."
              : "Submissions are approved on arrival and stamped 'auto-approved by rule' in the document history."} />

          {/* Say the quiet part out loud on methods where a person really was
              going to read something. Not blocked — just not silent. */}
          {fields.approval_mode === 'auto' && !autoApprovalRecommended(satisfiedBy) && (
            <p className="font-['Metropolis'] text-[11.5px] text-[#7A4E10] bg-[#E89A2E]/[0.10] border border-[#E89A2E]/35 rounded-lg px-3 py-2 mt-3 leading-snug">
              This document is a file the supplier provides — with auto-approval on, nobody opens it
              before it counts towards their compliance. Anything carrying an expiry date still goes
              to a person, because approving one confirms that date.
            </p>
          )}

          {fields.approval_mode === 'auto' && (
            <div className="mt-3">
              <Field
                label="Spot-check this percentage anyway"
                hint="Pulls a random share of submissions into the review queue so auto-approval never becomes no approval. 0% checks nothing.">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={fields.sample_review_pct ?? 10}
                    onChange={e => setField('sample_review_pct', e.target.value)}
                    className="font-['Metropolis'] w-24 bg-white border border-line rounded-lg text-ink px-3 py-2 text-sm focus:outline-none focus:border-[#92D400]" />
                  <span className="font-['Metropolis'] text-[13px] text-[#51534A]">%</span>
                </div>
              </Field>
              <p className="font-['Metropolis'] text-[11px] text-[#7C7F6F] mt-2 leading-snug">
                A submission still goes to a person if the supplier changed any of their prefilled
                details, or if it carries an expiry date — approving confirms that date, and only a
                person can check it against the document. Publishing a new version turns this back
                off, so new wording is always read once before it approves itself.
              </p>
            </div>
          )}
        </Section>
      )}

      <Section label="Enforcement" last>
        <Toggle
          checked={fields.block_expired_from_briefs}
          onChange={v => setField('block_expired_from_briefs', v)}
          label="Block suppliers with expired documents from new briefs"
          sub="They won't appear in supplier picker until this document is renewed." />
        <Toggle
          checked={fields.auto_flag_red_when_expired}
          onChange={v => setField('auto_flag_red_when_expired', v)}
          label="Auto-flag supplier as Red when expired"
          sub="Currently inherits from the supplier's worst expired document." />
      </Section>
    </>
  );
}

// =========================== sub-components ===========================

function Section({ label, last, children }) {
  return (
    <div className={`${last ? 'mb-0' : 'mb-7'}`}>
      <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A] pb-2 mb-3.5 border-b border-line">
        {label}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="font-['Metropolis'] text-[11px] font-semibold text-ink mb-1.5">{label}</div>
      {children}
      {hint && <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1.5 leading-relaxed">{hint}</div>}
    </div>
  );
}

const FIELD_INPUT_CLASS = "w-full bg-white border border-line rounded px-3.5 py-2.5 text-ink font-['Metropolis'] text-sm outline-none focus:border-[#92D400] transition-colors";

function Select({ value, onChange, options }) {
  return (
    <div className="relative">
      <select
        className={`${FIELD_INPUT_CLASS} appearance-none pr-9 cursor-pointer`}
        value={value}
        onChange={e => onChange(e.target.value)}>
        {options.map(o => (
          <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
        ))}
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#51534A" strokeWidth="2.5"
        className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
}

function NumberWithUnit({ value, unit, onValue, onUnit, units }) {
  return (
    <div className="flex gap-1.5">
      <input
        type="number"
        min="0"
        className={`${FIELD_INPUT_CLASS} flex-1`}
        value={value ?? ''}
        onChange={e => onValue(e.target.value === '' ? '' : Number(e.target.value))} />
      <div className="w-[110px] flex-shrink-0">
        <Select value={unit} onChange={onUnit} options={units} />
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, sub }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 border rounded text-left transition-colors mb-2 last:mb-0 ${checked
        ? 'bg-[#92D400]/[0.04] border-[#92D400]/30'
        : 'bg-white border-line hover:border-[#DADCCD]'}`}>
      <div className={`w-8 h-[18px] rounded-full relative flex-shrink-0 transition-colors ${checked ? 'bg-[#92D400]' : 'bg-[#51534A]'}`}>
        <span
          className="absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-[#F9FCF1] transition-transform"
          style={{ transform: checked ? 'translateX(14px)' : 'translateX(0)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-['Metropolis'] text-xs font-semibold text-ink">{label}</div>
        {sub && <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">{sub}</div>}
      </div>
    </button>
  );
}

function ReminderRuleRow({ rule, onChange, onRemove }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2.5 items-center p-3 bg-white border border-line rounded mb-2">
      <div className="flex items-center gap-1.5 flex-wrap font-['Metropolis'] text-xs text-ink">
        <span>Send reminder</span>
        {rule.timing_direction !== 'on' && (
          <>
            <input
              type="number"
              min="0"
              value={rule.timing_value}
              onChange={e => onChange({ timing_value: e.target.value === '' ? 0 : Number(e.target.value) })}
              className="w-14 bg-white border border-line rounded px-2 py-1 text-fusion-forest font-bold text-xs outline-none focus:border-[#92D400]" />
            <InlineSelect
              value={rule.timing_unit}
              onChange={v => onChange({ timing_unit: v })}
              options={REMINDER_TIMING_UNITS.map(u => ({ value: u, label: u }))} />
          </>
        )}
        <InlineSelect
          value={rule.timing_direction}
          onChange={v => onChange({ timing_direction: v })}
          options={REMINDER_DIRECTIONS} />
        <span>expiry</span>
        {rule.cadence !== 'one_time' && (
          <>
            <span className="text-[#51534A]">·</span>
            <InlineSelect
              value={rule.cadence}
              onChange={v => onChange({ cadence: v })}
              options={REMINDER_CADENCES} />
          </>
        )}
        {rule.cadence === 'one_time' && (
          <button
            type="button"
            onClick={() => onChange({ cadence: 'recurring_weekly' })}
            className="font-['Metropolis'] text-[10px] tracking-[0.1em] uppercase text-[#51534A] hover:text-active-green underline">
            + recurring
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove reminder rule"
        className="w-7 h-7 flex items-center justify-center bg-transparent border-none text-[#51534A] hover:text-[#E0290A] transition-colors">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

function InlineSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="bg-white border border-line rounded px-1.5 py-1 text-fusion-forest font-bold text-xs outline-none focus:border-[#92D400] cursor-pointer">
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
